import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { TenantContext } from '../context/TenantContext';
import API from '../api/axios';
import { getTenantFromSubdomain } from '../utils/tenant';
import { getTenantLabels } from '../utils/tenantTerms';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [tenantInfo, setTenantInfo] = useState(null);
  const [fetchingTenant, setFetchingTenant] = useState(false);

  const { tenantType } = useContext(TenantContext);
  const labels = getTenantLabels(tenantType);
  const { login, user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  const primaryColor = tenantInfo?.primary_color || '#0F172A';

  useEffect(() => {
    const fetchTenantDetails = async () => {
      const slug = getTenantFromSubdomain();
      if (!slug) return;

      setFetchingTenant(true);
      try {
        const res = await API.get(`/public/tenant-details?slug=${slug}`);
        if (res.data.success) {
          setTenantInfo(res.data.tenant);
        }
      } catch (err) {
        console.error('Failed to load tenant details:', err);
      } finally {
        setFetchingTenant(false);
      }
    };

    fetchTenantDetails();
  }, []);

  useEffect(() => {
    if (!loading && user) {
      const rolePaths = {
        super_admin: '/super-admin',
        university_admin: '/university-admin',
        college_admin: '/college-admin',
        hod: '/hod',
        coe: '/coe-dashboard',
        moderator: '/moderator',
        examiner: '/examiner',
        subject_matter_expert: '/subject-matter-expert',
      };
      navigate(rolePaths[user.role] || '/');
    }
  }, [loading, user, navigate]);

  if (fetchingTenant) {
    return (
      <div className="auth-split">
        <div className="auth-brand" style={{ backgroundColor: primaryColor }}>
          <div className="auth-brand-overlay" />
          <div className="auth-brand-content">
            <div className="spinner" style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#FFFFFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        </div>
        <div className="auth-console">
          <div className="auth-console-inner">
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Resolving workspace environment...</p>
          </div>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const data = await login(email, password, twoFactorCode);

      if (data.requires2FA) {
        setRequires2FA(true);
        setSubmitting(false);
        return;
      }

      // Users created with a temporary password are NOT logged in — send them to
      // the standalone reset screen, which redirects to login after they set a
      // new password.
      if (data.must_reset_password) {
        navigate('/force-reset-password', { state: { email: data.email || email, tempPassword: password } });
        return;
      }

      const rolePaths = {
        super_admin: '/super-admin',
        university_admin: '/university-admin',
        college_admin: '/college-admin',
        hod: '/hod',
        coe: '/coe-dashboard',
        moderator: '/moderator',
        examiner: '/examiner',
        subject_matter_expert: '/subject-matter-expert',
      };
      navigate(rolePaths[data.user.role] || '/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getLogoSrc = (logoUrl) => {
    const host = process.env.REACT_APP_DEV_API_URL || 'http://localhost:5000';
    return `${host}${logoUrl}`;
  };

  return (
    <div className="auth-split">
      {/* Left Panel — Dynamic Tenant Brand Block */}
      <div className="auth-brand" style={{ backgroundColor: primaryColor }}>
        <div className="auth-brand-overlay" />
        <div className="auth-brand-content">
          {tenantInfo ? (
            <>
              {tenantInfo.logo_url ? (
                <img
                  src={getLogoSrc(tenantInfo.logo_url)}
                  alt={tenantInfo.university_name}
                  className="auth-brand-logo"
                />
              ) : (
                <div className="auth-brand-logo-placeholder">
                  {tenantInfo.university_name.charAt(0).toUpperCase()}
                </div>
              )}
              <h2 className="auth-brand-name">{tenantInfo.university_name}</h2>
              {labels && (
                <span className="auth-brand-label">{labels.tenantLabel}</span>
              )}
              <div className="auth-brand-sub">
                <p className="auth-brand-sub-text">Secure Examination Infrastructure Portal</p>
                <p className="auth-brand-sub-note">Authorized Access Only. System sessions are ledger-audited.</p>
              </div>
            </>
          ) : (
            <>
              <div className="auth-brand-logo-placeholder" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF', width: 64, height: 64, fontSize: '1.75rem' }}>
                Q
              </div>
              <h2 className="auth-brand-name">Unified QPG</h2>
            </>
          )}
        </div>
      </div>

      {/* Right Panel — Secure Interactive Console */}
      <div className="auth-console" style={{ '--tenant-primary': primaryColor }}>
        <div className="auth-console-inner">
          {!tenantInfo && (
            <div className="auth-console-header">
              <h1 className="auth-title">Welcome Back</h1>
              <p className="auth-subtitle">Sign in to your account</p>
            </div>
          )}

          {error && <div className="form-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={requires2FA}
              />
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label className="form-label" style={{ marginBottom: 0 }}>Password</label>
                <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
              </div>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={requires2FA}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {requires2FA && (
              <div className="form-group">
                <label className="form-label">Two-Factor Authentication Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  required
                  autoFocus
                  style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                />
                <p style={{ fontSize: '0.875rem', color: '#475569', marginTop: '0.5rem' }}>
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
            )}

            {requires2FA && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%', marginBottom: '1rem' }}
                onClick={() => {
                  setRequires2FA(false);
                  setTwoFactorCode('');
                }}
              >
                Back
              </button>
            )}

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="auth-link">
            Don't have an account? <Link to="/register">Register</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
